/**
 * Visual mocks for documentation pages.
 *
 * Matches the dark-theme feature-page mock style: #1e1e2e bg, #181825 cards,
 * border-white/[0.06], font-mono, and uses the actual platform UI patterns,
 * metric names, and layout structures.
 */
import { type FC, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCode,
  faChartLine,
  faFlask,
  faChartColumn,
  faFolder,
  faBrain,
  faCheckCircle,
  faTimesCircle,
  faArrowsRotate,
  faSlidersH,
  faInfoCircle,
  faExpand,
  faCalendar,
  faChartBar,
  faArrowLeft,
  faPause,
  faStop,
  faCog,
  faLayerGroup,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';

/* ═══════════════════════════════════════════════════════════
   Shared helpers
   ═══════════════════════════════════════════════════════════ */

/** Outer shell wrapping every diagram mock */
const DiagramShell: FC<{ bg?: string; children: ReactNode }> = ({ bg = '#1e1e2e', children }) => (
  <div
    className="my-6 rounded-lg overflow-hidden shadow-2xl shadow-black/30 border border-white/[0.06] font-mono"
    style={{ background: bg }}
  >
    {children}
  </div>
);

/** Standard dark card used inside diagrams */
const MockCard: FC<{ className?: string; children: ReactNode }> = ({ className = '', children }) => (
  <div className={`bg-[#181825] rounded-lg border border-white/[0.06] p-4 ${className}`}>
    {children}
  </div>
);

/** Metric cell: label / large value / optional sub-text */
const MetricCell: FC<{ label: string; value: string; sub?: string; color?: string }> = ({
  label, value, sub, color = 'text-white',
}) => (
  <div className="px-4 py-3 bg-[#181825]">
    <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">{label}</div>
    <div className={`text-lg font-semibold ${color}`}>{value}</div>
    {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   Research Lab — IDE layout matching AppShell + IDELayout
   ═══════════════════════════════════════════════════════════ */
const ResearchLabDiagram: FC = () => (
  <DiagramShell bg="#0a0e14">
    <div className="flex min-h-[260px]">
      {/* Activity Rail — matches ActivityRail.tsx (56px, icon-only) */}
      <div className="w-14 border-r border-white/[0.06] flex flex-col items-center py-3 gap-1 shrink-0" style={{ background: '#0a0e14' }}>
        {[
          { icon: faFolder, label: 'Projects', active: false },
          { icon: faCode, label: 'Editor', active: true },
          { icon: faFlask, label: 'Analyze', active: false },
          { icon: faChartColumn, label: 'Results', active: false },
          { icon: faBrain, label: 'Intelligence', active: false },
        ].map((item) => (
          <div
            key={item.label}
            className={`w-full flex flex-col items-center gap-0.5 py-2 text-[8px] ${item.active
              ? 'text-[#9d4edd] bg-[#0f1923] border-l-2 border-[#9d4edd]'
              : 'text-gray-500'
              }`}
          >
            <FontAwesomeIcon icon={item.icon} className="text-[11px]" />
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — matches AppTopBar.tsx */}
        <div className="flex items-center px-3 py-2 border-b border-white/[0.06]" style={{ background: '#181825' }}>
          <img src="/quanthop_logo_dark.svg" alt="" className="h-4 opacity-60" />
          <span className="text-[10px] text-gray-500 ml-3">EMA Crossover Project</span>
        </div>

        {/* Workspace tabs — matches ResearchLabTabs.tsx */}
        <div className="flex items-center gap-0 border-b border-white/[0.06]" style={{ background: '#181825' }}>
          {[
            { icon: faCode, label: 'Code', active: true },
            { icon: faChartLine, label: 'Analyze', active: false },
            { icon: faCog, label: 'Optimize', active: false },
            { icon: faLayerGroup, label: 'WFA', active: false },
          ].map((tab) => (
            <div
              key={tab.label}
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] border-b-2 ${tab.active
                ? 'text-[#9d4edd] border-[#9d4edd]'
                : 'text-gray-500 border-transparent'
                }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="text-[9px]" />
              {tab.label}
            </div>
          ))}
        </div>

        {/* IDE body: editor + right dock */}
        <div className="flex flex-1 min-h-0">
          {/* Code editor — VS Code Dark+ palette */}
          <div className="flex-1 p-4 text-[11px] leading-[1.65] overflow-hidden" style={{ background: '#1e1e2e' }}>
            <div><span style={{ color: '#546E3F' }}>{'// EMA Crossover Strategy'}</span></div>
            <div className="mt-1.5"><span style={{ color: '#c084fc' }}>function</span> <span style={{ color: '#DCDCAA' }}>define</span><span style={{ color: '#f9fafb' }}>(ctx) {'{'}</span></div>
            <div>  <span style={{ color: '#9CDCFE' }}>ctx</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#DCDCAA' }}>param</span><span style={{ color: '#f9fafb' }}>(</span><span style={{ color: '#CE9178' }}>&apos;fastLength&apos;</span><span style={{ color: '#f9fafb' }}>, {'{'} </span><span style={{ color: '#9CDCFE' }}>type</span><span style={{ color: '#f9fafb' }}>: </span><span style={{ color: '#CE9178' }}>&apos;int&apos;</span><span style={{ color: '#f9fafb' }}>, </span><span style={{ color: '#9CDCFE' }}>default</span><span style={{ color: '#f9fafb' }}>: </span><span style={{ color: '#B5CEA8' }}>9</span><span style={{ color: '#f9fafb' }}> {'}'});</span></div>
            <div>  <span style={{ color: '#9CDCFE' }}>ctx</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#DCDCAA' }}>param</span><span style={{ color: '#f9fafb' }}>(</span><span style={{ color: '#CE9178' }}>&apos;slowLength&apos;</span><span style={{ color: '#f9fafb' }}>, {'{'} </span><span style={{ color: '#9CDCFE' }}>type</span><span style={{ color: '#f9fafb' }}>: </span><span style={{ color: '#CE9178' }}>&apos;int&apos;</span><span style={{ color: '#f9fafb' }}>, </span><span style={{ color: '#9CDCFE' }}>default</span><span style={{ color: '#f9fafb' }}>: </span><span style={{ color: '#B5CEA8' }}>21</span><span style={{ color: '#f9fafb' }}> {'}'});</span></div>
            <div><span style={{ color: '#f9fafb' }}>{'}'}</span></div>
            <div className="mt-1.5"><span style={{ color: '#c084fc' }}>function</span> <span style={{ color: '#DCDCAA' }}>onBar</span><span style={{ color: '#f9fafb' }}>(ctx, i) {'{'}</span></div>
            <div>  <span style={{ color: '#c084fc' }}>if</span><span style={{ color: '#f9fafb' }}> (</span><span style={{ color: '#9CDCFE' }}>q</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#DCDCAA' }}>crossOver</span><span style={{ color: '#f9fafb' }}>(</span><span style={{ color: '#9CDCFE' }}>ctx</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#9CDCFE' }}>fastEMA</span><span style={{ color: '#f9fafb' }}>, </span><span style={{ color: '#9CDCFE' }}>ctx</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#9CDCFE' }}>slowEMA</span><span style={{ color: '#f9fafb' }}>, i))</span></div>
            <div>    <span style={{ color: '#9CDCFE' }}>ctx</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#9CDCFE' }}>order</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#DCDCAA' }}>market</span><span style={{ color: '#f9fafb' }}>(</span><span style={{ color: '#CE9178' }}>&apos;long&apos;</span><span style={{ color: '#f9fafb' }}>);</span></div>
            <div><span style={{ color: '#f9fafb' }}>{'}'}</span></div>
          </div>

          {/* Right dock — matches IDELayout right dock */}
          <div className="w-44 border-l border-white/[0.06] flex flex-col shrink-0" style={{ background: '#181825' }}>
            <div className="flex flex-wrap border-b border-white/[0.06]">
              {['Config', 'Jobs'].map((tab, i) => (
                <span
                  key={tab}
                  className={`px-2.5 py-1.5 text-[9px] font-medium ${i === 0 ? 'text-[#9d4edd] border-b border-[#9d4edd]' : 'text-gray-500'
                    }`}
                >
                  {tab}
                </span>
              ))}
            </div>
            <div className="p-3 flex flex-col gap-2 text-[10px]">
              <div className="flex justify-between">
                <span className="text-gray-500">Symbol</span>
                <span className="text-gray-300 font-medium">BTCUSDT</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Interval</span>
                <span className="text-gray-300 font-medium">4h</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Mode</span>
                <span className="text-gray-300 font-medium">Long Only</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Capital</span>
                <span className="text-gray-300 font-medium">10,000</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom status bar */}
        <div className="flex items-center justify-between px-4 py-1 border-t border-white/[0.06] text-[9px] text-gray-500" style={{ background: '#181825' }}>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-400 text-[8px]" />
              0 errors
            </span>
            <span>0 warnings</span>
          </div>
          <span>QSL v1.0</span>
        </div>
      </div>
    </div>
  </DiagramShell>
);

/* ═══════════════════════════════════════════════════════════
   Running a Backtest — Config panel matching BasicSettingsSection
   ═══════════════════════════════════════════════════════════ */
const BacktestConfigDiagram: FC = () => (
  <DiagramShell>
    {/* Header */}
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.06]" style={{ background: '#181825' }}>
      <FontAwesomeIcon icon={faFlask} className="text-xs text-blue-400" />
      <span className="text-sm font-medium text-gray-300 uppercase tracking-wide">Backtest Configuration</span>
    </div>

    <div className="p-4 space-y-4">
      {/* Row 1: Strategy + Symbol */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[9px] text-gray-500 uppercase tracking-wider mb-1">Strategy</label>
          <div className="flex items-center px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.03] text-[11px] text-gray-300">
            EMA Crossover
            <svg className="ml-auto w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
        </div>
        <div>
          <label className="block text-[9px] text-gray-500 uppercase tracking-wider mb-1">Symbol</label>
          <div className="flex items-center px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.03] text-[11px] text-gray-300">
            BTCUSDT
            <svg className="ml-auto w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
        </div>
      </div>

      {/* Row 2: Interval + Trading Mode */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[9px] text-gray-500 uppercase tracking-wider mb-1">Interval</label>
          <div className="flex items-center px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.03] text-[11px] text-gray-300">
            4h
            <svg className="ml-auto w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
        </div>
        <div>
          <label className="block text-[9px] text-gray-500 uppercase tracking-wider mb-1">Trading Mode</label>
          <div className="flex gap-1">
            {['Long Only', 'Short Only', 'Bidirectional'].map((mode, i) => (
              <span
                key={mode}
                className={`px-2 py-1.5 rounded text-[10px] font-medium ${i === 0 ? 'bg-[#aa48ff] text-white shadow-sm' : 'text-gray-400 bg-white/[0.03]'
                  }`}
              >
                {mode}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: Date range — matching DateRangeSection toggle */}
      <div>
        <label className="block text-[9px] text-gray-500 uppercase tracking-wider mb-1">Range</label>
        <div className="grid grid-cols-2 gap-1 mb-2 max-w-[240px]">
          <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-medium bg-[#aa48ff] text-white shadow-sm">
            <FontAwesomeIcon icon={faCalendar} className="text-[9px]" />
            Date Range
          </div>
          <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-medium text-gray-400 bg-white/[0.03]">
            <FontAwesomeIcon icon={faChartBar} className="text-[9px]" />
            Candle Range
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.03] text-[11px] text-gray-400">
            2021-01-01
          </div>
          <div className="px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.03] text-[11px] text-gray-400">
            2024-12-31
          </div>
        </div>
      </div>

      {/* Row 4: Portfolio */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-[9px] text-gray-500 uppercase tracking-wider mb-1">Portfolio Amount</label>
          <div className="px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.03] text-[11px] text-gray-300 font-medium">10,000</div>
        </div>
        <div>
          <label className="block text-[9px] text-gray-500 uppercase tracking-wider mb-1">Currency</label>
          <div className="px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.03] text-[11px] text-gray-300">USDT</div>
        </div>
        <div>
          <label className="block text-[9px] text-gray-500 uppercase tracking-wider mb-1">Portfolio Type</label>
          <div className="flex gap-1">
            {['USD', 'Asset', 'Mixed'].map((t, i) => (
              <span key={t} className={`px-2 py-1.5 rounded text-[10px] font-medium ${i === 0 ? 'bg-[#aa48ff] text-white' : 'text-gray-400 bg-white/[0.03]'}`}>{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Run button */}
    <div className="px-4 pb-4">
      <div className="w-full py-2.5 text-center rounded-lg text-[12px] font-medium text-white" style={{ backgroundColor: '#aa48ff' }}>
        Run Backtest
      </div>
    </div>
  </DiagramShell>
);

/* ═══════════════════════════════════════════════════════════
   Reading Results — Matching BaselinePerformanceMock + MetricsGridCard
   ═══════════════════════════════════════════════════════════ */
const BacktestResultsDiagram: FC = () => (
  <DiagramShell bg="#0f1923">
    <div className="p-3">
      <div className="bg-[#181825] border border-white/[0.06] rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faChartLine} className="text-sm text-blue-400" />
            <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wide">At a Glance</h3>
          </div>
          <span className="text-[10px] text-gray-500">BTCUSDT &middot; 4h &middot; 2021-2024</span>
        </div>

        {/* Metrics grid — matches MetricsGridCard structure */}
        <div className="grid grid-cols-3 gap-px bg-white/[0.03]">
          <MetricCell label="Payoff" value="2.56x" sub="Avg win / Avg loss" />
          <MetricCell label="Profit Factor" value="1.87" sub="Gross P / Gross L" />
          <MetricCell label="Sharpe Ratio" value="1.24" sub="Risk-adjusted" />
          <MetricCell label="Win Rate" value="42.3%" sub="68 / 161 trades" />
          <MetricCell label="Expectancy" value="+0.42%" sub="Per trade" color="text-emerald-400" />
          <MetricCell label="Max Drawdown" value="-18.4%" sub="Peak to trough" color="text-amber-400" />
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-white/[0.06] flex items-center justify-between text-[10px] text-gray-500">
          <span>Total Return: +112.4%</span>
          <span>161 trades &middot; 1,095 days</span>
        </div>
      </div>

      {/* Additional cards row — matches the 4x2 grid card layout */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        {/* Drawdown card */}
        <MockCard>
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/[0.06]">
            <FontAwesomeIcon icon={faChartLine} className="text-xs text-red-400" />
            <span className="text-[10px] font-semibold text-gray-300 uppercase tracking-wide flex-1">Drawdown</span>
            <FontAwesomeIcon icon={faInfoCircle} className="text-[9px] text-gray-500" />
            <FontAwesomeIcon icon={faExpand} className="text-[9px] text-gray-500" />
          </div>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-base font-bold text-red-400">-18.4%</span>
            <span className="text-[9px] text-gray-500">depth</span>
          </div>
          <div className="space-y-1 mt-2 text-[10px]">
            <div className="flex justify-between">
              <span className="text-gray-500">Duration</span>
              <span className="text-gray-300">42 bars</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Recovery</span>
              <span className="text-gray-300">2.1x</span>
            </div>
          </div>
        </MockCard>

        {/* Regimes card — matches RegimesGridCard */}
        <MockCard>
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/[0.06]">
            <FontAwesomeIcon icon={faChartBar} className="text-xs text-purple-400" />
            <span className="text-[10px] font-semibold text-gray-300 uppercase tracking-wide flex-1">Regimes</span>
            <FontAwesomeIcon icon={faInfoCircle} className="text-[9px] text-gray-500" />
            <FontAwesomeIcon icon={faExpand} className="text-[9px] text-gray-500" />
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-[8px] text-gray-500 uppercase tracking-wider mb-1">Volatility</p>
              <div className="flex h-3 rounded overflow-hidden">
                <div className="bg-blue-500/40" style={{ width: '35%' }} />
                <div className="bg-amber-500/40" style={{ width: '45%' }} />
                <div className="bg-red-500/40" style={{ width: '20%' }} />
              </div>
            </div>
            <div>
              <p className="text-[8px] text-gray-500 uppercase tracking-wider mb-1">Trend</p>
              <div className="flex h-3 rounded overflow-hidden">
                <div className="bg-gray-500/40" style={{ width: '40%' }} />
                <div className="bg-purple-500/40" style={{ width: '25%' }} />
                <div className="bg-emerald-500/40" style={{ width: '35%' }} />
              </div>
            </div>
          </div>
        </MockCard>
      </div>
    </div>
  </DiagramShell>
);

/* ═══════════════════════════════════════════════════════════
   WFA — Matching WFAWindowMock exactly
   ═══════════════════════════════════════════════════════════ */
const WFAWindowsDiagram: FC = () => (
  <DiagramShell bg="#0f1923">
    <div className="p-3">
      <div className="bg-[#181825] border border-white/[0.06] rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faArrowsRotate} className="text-sm text-cyan-400" />
            <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wide">Walk-Forward Analysis</h3>
          </div>
          <span className="text-[10px] text-gray-500">5 windows &middot; Anchored</span>
        </div>

        {/* Window timeline — matches WFAWindowMock exactly */}
        <div className="space-y-2 mb-4">
          {[
            { win: 1, oosReturn: '+8.2%', sharpe: '1.14', pass: true },
            { win: 2, oosReturn: '+5.7%', sharpe: '0.98', pass: true },
            { win: 3, oosReturn: '+12.1%', sharpe: '1.41', pass: true },
            { win: 4, oosReturn: '+3.8%', sharpe: '0.72', pass: false },
            { win: 5, oosReturn: '+9.4%', sharpe: '1.18', pass: true },
          ].map((w) => (
            <div key={w.win} className="flex items-center gap-3 px-3 py-2 rounded bg-white/[0.02]">
              <span className="text-[10px] text-gray-500 w-6">W{w.win}</span>
              <div className="flex-1 h-3 rounded-sm bg-blue-500/20 relative">
                <span className="absolute inset-0 flex items-center justify-center text-[8px] text-blue-400/70">IS</span>
              </div>
              <div className={`w-20 h-3 rounded-sm ${w.pass ? 'bg-emerald-500/20' : 'bg-rose-500/20'} relative`}>
                <span className={`absolute inset-0 flex items-center justify-center text-[8px] ${w.pass ? 'text-emerald-400/70' : 'text-rose-400/70'}`}>OOS</span>
              </div>
              <span className={`text-[10px] w-12 text-right ${w.pass ? 'text-emerald-400' : 'text-rose-400'}`}>{w.oosReturn}</span>
              <span className="text-[10px] text-gray-400 w-10 text-right">{w.sharpe}</span>
              <FontAwesomeIcon
                icon={w.pass ? faCheckCircle : faTimesCircle}
                className={`text-[10px] ${w.pass ? 'text-emerald-400' : 'text-rose-400'}`}
              />
            </div>
          ))}
        </div>

        {/* WFA Summary — matches WFAWindowMock */}
        <div className="grid grid-cols-4 gap-3 px-3 py-2 rounded bg-white/[0.03] border border-white/[0.04]">
          {[
            { label: 'WFE', value: '72%', color: 'text-emerald-400' },
            { label: 'OOS Sharpe', value: '1.09', color: 'text-white' },
            { label: 'Pass Rate', value: '4/5', color: 'text-emerald-400' },
            { label: 'Verdict', value: 'Pass', color: 'text-emerald-400' },
          ].map((m) => (
            <div key={m.label} className="text-center">
              <div className="text-[9px] text-gray-500 uppercase tracking-wider">{m.label}</div>
              <div className={`text-sm font-semibold ${m.color}`}>{m.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </DiagramShell>
);

/* ═══════════════════════════════════════════════════════════
   Optimization Results — Matching StabilityRefinementMock
   ═══════════════════════════════════════════════════════════ */
const OptimizationResultsDiagram: FC = () => (
  <DiagramShell bg="#0f1923">
    {/* Header */}
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]" style={{ background: '#0c1520' }}>
      <div className="flex items-center gap-2">
        <FontAwesomeIcon icon={faSlidersH} className="text-sm text-gray-400" />
        <span className="text-sm font-semibold text-white">Stability Refinement</span>
      </div>
      <span className="text-gray-500 text-xs">EMA Cross &mdash; BTCUSDT 4h</span>
    </div>

    <div className="p-3 grid grid-cols-2 gap-3">
      {/* Left: Primary Stable Region */}
      <div className="space-y-3">
        <div className="rounded-lg bg-emerald-900/20 border border-emerald-800/40 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-400 text-xs" />
            <span className="text-xs font-semibold text-emerald-300 uppercase tracking-wide">Primary Stable Region</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Combinations', value: '24', color: 'text-white' },
              { label: 'Robustness', value: '78%', color: 'text-emerald-300' },
              { label: 'Variance', value: '0.12', color: 'text-white' },
              { label: 'Sensitivity', value: 'Low', color: 'text-white' },
            ].map((s) => (
              <div key={s.label} className="bg-white/[0.04] rounded p-2">
                <p className="text-[8px] text-gray-500 uppercase tracking-wider">{s.label}</p>
                <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Selected Parameters */}
        <div className="bg-white/[0.03] rounded-lg p-3">
          <h4 className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Selected Parameters</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-[9px] text-gray-500">Fast Length</span>
              <p className="text-base font-semibold text-white">12</p>
            </div>
            <div>
              <span className="text-[9px] text-gray-500">Slow Length</span>
              <p className="text-base font-semibold text-white">24</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Parameter Stability Map */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[10px] font-semibold text-gray-300 uppercase tracking-wide">Parameter Stability Map</h3>
          <div className="flex items-center gap-0.5 bg-white/10 rounded-md p-0.5">
            <span className="px-2 py-0.5 rounded text-[9px] font-medium bg-white/20 text-gray-100 shadow-sm">Heatmap</span>
            <span className="px-2 py-0.5 rounded text-[9px] font-medium text-gray-400">Lines</span>
            <span className="px-2 py-0.5 rounded text-[9px] font-medium text-gray-400">Strips</span>
          </div>
        </div>
        <div className="bg-white/[0.03] rounded-lg p-3 space-y-3">
          {/* Fast Length strip */}
          <div className="flex items-center gap-2">
            <span className="text-[8px] text-gray-400 w-14 text-right shrink-0">Fast Length</span>
            <div className="flex-1 flex gap-[2px]">
              {[
                'bg-gray-600/40', 'bg-gray-600/40', 'bg-gray-600/40',
                'bg-emerald-600/50', 'bg-emerald-500/60', 'bg-emerald-400/80',
                'bg-emerald-400/90', 'bg-emerald-400/80', 'bg-emerald-500/60',
                'bg-gray-600/40', 'bg-gray-600/40', 'bg-gray-600/40',
              ].map((c, i) => (
                <div key={i} className="relative flex-1">
                  <div className={`h-5 rounded-[2px] ${c}`} />
                  {i === 6 && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-1.5 h-3 bg-white rounded-full opacity-90" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Slow Length strip */}
          <div className="flex items-center gap-2">
            <span className="text-[8px] text-gray-400 w-14 text-right shrink-0">Slow Length</span>
            <div className="flex-1 flex gap-[2px]">
              {[
                'bg-emerald-600/50', 'bg-emerald-500/60', 'bg-emerald-500/70',
                'bg-emerald-400/80', 'bg-emerald-400/90', 'bg-emerald-400/80',
                'bg-emerald-500/60', 'bg-emerald-600/50', 'bg-gray-600/40',
                'bg-gray-600/40', 'bg-gray-600/40', 'bg-gray-600/40',
              ].map((c, i) => (
                <div key={i} className="relative flex-1">
                  <div className={`h-5 rounded-[2px] ${c}`} />
                  {i === 3 && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-1.5 h-3 bg-white rounded-full opacity-90" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 text-[7px] text-gray-400">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-emerald-400/70" />
              <span>Stable region</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded bg-emerald-400" />
              <span>Selected value</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded bg-gray-600/50" />
              <span>Other results</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Footer */}
    <div className="flex items-center justify-end gap-3 px-4 py-2 border-t border-white/[0.06]" style={{ background: '#0c1520' }}>
      <span className="text-[10px] text-gray-500">Cancel</span>
      <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium text-white rounded-lg" style={{ backgroundColor: '#aa48ff' }}>
        Proceed with Robust Selection
        <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
      </div>
    </div>
  </DiagramShell>
);

/* ═══════════════════════════════════════════════════════════
   Adaptive Flow — Matching AdaptiveFlowHeroMock
   ═══════════════════════════════════════════════════════════ */
const AdaptiveFlowDiagram: FC = () => (
  <DiagramShell bg="#0a0e14">
    {/* Top bar — matches AdaptiveFlowTopBar.tsx */}
    <div className="h-[60px] border-b border-white/[0.06] flex items-center justify-between px-4" style={{ background: '#0c1520' }}>
      {/* Left: Strategy Identity */}
      <div className="flex items-center gap-3">
        <FontAwesomeIcon icon={faArrowLeft} className="text-xs text-gray-500" />
        <div>
          <div className="flex items-center gap-2 text-[11px]">
            <span className="text-white font-semibold">BTCUSDT</span>
            <span className="text-gray-500">&middot;</span>
            <span className="text-gray-300">1d</span>
            <span className="text-gray-500">&middot;</span>
            <span className="text-cyan-400 font-mono font-medium">$67,160</span>
          </div>
          <div className="flex items-center gap-2 text-[10px]">
            <span className="text-gray-400">EMA Crossover Project</span>
            <span className="text-gray-500">v1</span>
          </div>
        </div>
      </div>

      {/* Right: KPIs + Controls */}
      <div className="flex items-center gap-3">
        <div className="flex flex-col items-center px-3">
          <span className="text-xl font-bold text-emerald-400">7.4</span>
          <span className="text-[8px] text-gray-400 uppercase tracking-wide">Health</span>
        </div>
        <div className="flex flex-col items-center px-3">
          <span className="text-xl font-bold text-white">78%</span>
          <span className="text-[8px] text-gray-400 uppercase tracking-wide">Confidence</span>
        </div>
        <span className="flex items-center gap-1.5 px-2 py-1 rounded" style={{ background: 'rgba(16,185,129,0.1)' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[9px] text-gray-300">Active</span>
        </span>
        <div className="flex items-center gap-1">
          <span className="flex items-center gap-1 px-2 py-1 text-[9px] text-gray-400 border border-gray-600/30 rounded">
            <FontAwesomeIcon icon={faPause} className="text-[8px]" /> Pause
          </span>
          <span className="flex items-center gap-1 px-2 py-1 text-[9px] text-gray-400 border border-gray-600/30 rounded">
            <FontAwesomeIcon icon={faStop} className="text-[8px]" /> Stop
          </span>
        </div>
      </div>
    </div>

    {/* Expected vs Actual — matches ExpectedVsActualPanel */}
    <div className="bg-[#181825] border border-white/[0.06] rounded-lg p-4 m-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-white uppercase tracking-wide">Live Performance vs Expected</h3>
        <div className="text-[10px] text-gray-400">
          Live Trades: <span className="text-white font-medium">12</span>
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto">
        {[
          { label: 'Win Rate', expected: '40.0%', range: '40.0 - 40.0%', actual: '41.7%', color: 'text-emerald-400' },
          { label: 'Sharpe Ratio', expected: '1.00', range: '0.99 - 1.02', actual: '1.12', color: 'text-emerald-400' },
          { label: 'Max Drawdown', expected: '21.9%', range: '21.9 - 21.9%', actual: '14.2%', color: 'text-emerald-400' },
          { label: 'Profit Factor', expected: '4.64', range: '3.72 - 5.57', actual: '3.91', color: 'text-white' },
          { label: 'Avg Return', expected: '26.8%', range: '26.8 - 27.1%', actual: '28.3%', color: 'text-emerald-400' },
        ].map((m) => (
          <div key={m.label} className="bg-white/[0.03] rounded-lg p-3 flex-1 min-w-[100px]">
            <div className="text-[8px] text-gray-400 uppercase tracking-wide mb-1">{m.label}</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-semibold text-white">{m.expected}</span>
              <span className="text-[8px] text-gray-500">({m.range})</span>
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="text-[9px] text-gray-500">Actual:</span>
              <span className={`text-[11px] font-medium ${m.color}`}>{m.actual}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-[10px] text-emerald-400">
        Within expected range &mdash; all metrics within validation bounds.
      </div>
    </div>
  </DiagramShell>
);

/* ═══════════════════════════════════════════════════════════
   Comparing Runs — Matching ComparisonSummaryPanel
   ═══════════════════════════════════════════════════════════ */
const ComparingRunsDiagram: FC = () => (
  <DiagramShell bg="#0f1923">
    <div className="p-3">
      <div className="bg-[#181825] border border-white/[0.06] rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faChartColumn} className="text-sm text-blue-400" />
            <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wide">Comparison Summary</h3>
          </div>
          <span className="px-2 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-emerald-500/15 text-emerald-400">Improved</span>
        </div>

        {/* Metric categories — matching ComparisonSummaryPanel */}
        <div className="space-y-4">
          {/* Risk-Adjusted */}
          <div>
            <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">Risk-Adjusted</p>
            <div className="space-y-1.5">
              {[
                { label: 'Sharpe', from: '1.24', to: '1.52', improved: true },
                { label: 'Max Drawdown', from: '-18.4%', to: '-14.2%', improved: true },
                { label: 'Calmar', from: '0.82', to: '1.04', improved: true },
              ].map((m) => (
                <div key={m.label} className="flex items-center gap-3 text-[10px]">
                  <span className="text-gray-400 w-24">{m.label}</span>
                  <span className="text-gray-500 w-12 text-right">{m.from}</span>
                  <FontAwesomeIcon icon={faArrowRight} className="text-[7px] text-gray-600" />
                  <span className="text-white w-12 font-medium">{m.to}</span>
                  <span className={`text-[9px] ${m.improved ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {m.improved ? '+' : '-'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Efficiency */}
          <div>
            <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">Efficiency</p>
            <div className="space-y-1.5">
              {[
                { label: 'Profit Factor', from: '1.87', to: '2.14', improved: true },
                { label: 'Payoff Ratio', from: '2.56', to: '2.81', improved: true },
                { label: 'Expectancy', from: '+0.42%', to: '+0.58%', improved: true },
              ].map((m) => (
                <div key={m.label} className="flex items-center gap-3 text-[10px]">
                  <span className="text-gray-400 w-24">{m.label}</span>
                  <span className="text-gray-500 w-12 text-right">{m.from}</span>
                  <FontAwesomeIcon icon={faArrowRight} className="text-[7px] text-gray-600" />
                  <span className="text-white w-12 font-medium">{m.to}</span>
                  <span className="text-[9px] text-emerald-400">+</span>
                </div>
              ))}
            </div>
          </div>

          {/* Parameter deltas */}
          <div className="pt-2 border-t border-white/[0.06]">
            <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">Parameter Changes</p>
            <div className="flex gap-2">
              <span className="px-2 py-1 rounded text-[9px] bg-purple-900/30 text-purple-400">
                fastLength: 9 &rarr; 12
              </span>
              <span className="px-2 py-1 rounded text-[9px] bg-purple-900/30 text-purple-400">
                slowLength: 21 &rarr; 24
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DiagramShell>
);

/* ═══════════════════════════════════════════════════════════
   Strategy Lifecycle — QSL execution flow matching QSLEditorMock style
   ═══════════════════════════════════════════════════════════ */
const StrategyLifecycleDiagram: FC = () => (
  <DiagramShell>
    {/* Editor header */}
    <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.06]" style={{ background: '#181825' }}>
      <div className="flex items-center gap-2 text-xs">
        <FontAwesomeIcon icon={faCode} className="text-[#9d4edd]/60" />
        <span className="text-gray-400">strategy-lifecycle.qsl</span>
        <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/15 text-emerald-400 uppercase tracking-wider">Validated</span>
      </div>
      <span className="text-[10px] text-gray-500">QSL v1.0</span>
    </div>

    <div className="p-4">
      {/* 3-phase flow */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* define */}
        <div className="flex-1 rounded-lg bg-[#9d4edd]/10 border border-[#9d4edd]/20 p-3">
          <p className="text-[10px] font-bold text-[#9d4edd] mb-1">
            <span style={{ color: '#c084fc' }}>function</span>{' '}
            <span style={{ color: '#DCDCAA' }}>define</span>
            <span style={{ color: '#f9fafb' }}>(ctx)</span>
          </p>
          <p className="text-[9px] text-gray-400 leading-relaxed mb-2">Declare optimizable parameters with type, default, min, max</p>
          <div className="text-[10px]">
            <div><span style={{ color: '#9CDCFE' }}>ctx</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#DCDCAA' }}>param</span><span style={{ color: '#f9fafb' }}>(</span><span style={{ color: '#CE9178' }}>&apos;period&apos;</span><span style={{ color: '#f9fafb' }}>, {'{...}'})</span></div>
          </div>
          <p className="text-[8px] text-gray-500 mt-2">Called once</p>
        </div>

        <div className="hidden sm:flex items-center text-gray-600 text-sm">&rarr;</div>

        {/* init */}
        <div className="flex-1 rounded-lg bg-blue-500/10 border border-blue-500/20 p-3">
          <p className="text-[10px] font-bold text-blue-400 mb-1">
            <span style={{ color: '#c084fc' }}>function</span>{' '}
            <span style={{ color: '#DCDCAA' }}>init</span>
            <span style={{ color: '#f9fafb' }}>(ctx)</span>
          </p>
          <p className="text-[9px] text-gray-400 leading-relaxed mb-2">Register indicators using parameters from define()</p>
          <div className="text-[10px]">
            <div><span style={{ color: '#9CDCFE' }}>ctx</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#DCDCAA' }}>indicator</span><span style={{ color: '#f9fafb' }}>(</span><span style={{ color: '#CE9178' }}>&apos;ema&apos;</span><span style={{ color: '#f9fafb' }}>, ...)</span></div>
          </div>
          <p className="text-[8px] text-gray-500 mt-2">Called once</p>
        </div>

        <div className="hidden sm:flex items-center text-gray-600 text-sm">&rarr;</div>

        {/* onBar */}
        <div className="flex-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 relative">
          <p className="text-[10px] font-bold text-emerald-400 mb-1">
            <span style={{ color: '#c084fc' }}>function</span>{' '}
            <span style={{ color: '#DCDCAA' }}>onBar</span>
            <span style={{ color: '#f9fafb' }}>(ctx, i)</span>
          </p>
          <p className="text-[9px] text-gray-400 leading-relaxed mb-2">Execute trading logic for each candle</p>
          <div className="text-[10px]">
            <div><span style={{ color: '#9CDCFE' }}>ctx</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#9CDCFE' }}>order</span><span style={{ color: '#f9fafb' }}>.</span><span style={{ color: '#DCDCAA' }}>market</span><span style={{ color: '#f9fafb' }}>(</span><span style={{ color: '#CE9178' }}>&apos;long&apos;</span><span style={{ color: '#f9fafb' }}>)</span></div>
          </div>
          <p className="text-[8px] text-gray-500 mt-2">Called N times &middot; <span className="text-emerald-400/60">&#x21bb; loop</span></p>
        </div>
      </div>
    </div>

    {/* Bottom status */}
    <div className="flex items-center justify-between px-4 py-1.5 border-t border-white/[0.06] text-[10px] text-gray-500" style={{ background: '#181825' }}>
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-1">
          <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-400 text-[9px]" />
          0 errors
        </span>
        <span>0 warnings</span>
      </div>
      <span>3 lifecycle hooks</span>
    </div>
  </DiagramShell>
);

/* ═══════════════════════════════════════════════════════════
   Registry — map page IDs to diagram components
   ═══════════════════════════════════════════════════════════ */
const DOC_DIAGRAMS: Record<string, FC> = {
  'dashboard/research-lab': ResearchLabDiagram,
  'backtesting/running-a-backtest': BacktestConfigDiagram,
  'backtesting/reading-results': BacktestResultsDiagram,
  'backtesting/comparing-runs': ComparingRunsDiagram,
  'strategy-optimization/reading-optimization-results': OptimizationResultsDiagram,
  'strategy-optimization/walk-forward-analysis': WFAWindowsDiagram,
  'strategy-optimization/adaptive-flow': AdaptiveFlowDiagram,
  'getting-started/strategy-lifecycle': StrategyLifecycleDiagram,
};

export default DOC_DIAGRAMS;
